import { Injectable, OnDestroy, RendererFactory2 } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
	VerfahrenFacade,
	VerfahrenSearchKey,
	VerfahrenSearchQuery,
	VerfahrenStatus,
} from '@schir-int-client/verfahren-shared';
import { isNil } from 'lodash-es';
import { Subject, Subscription } from 'rxjs';
import { ApiRootFacade, ApiRootLinkRel } from '@schir-int-client/api-root';
import { filter } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class VerfahrenGlobalSearchFormService implements OnDestroy {
	readonly form: UntypedFormGroup;

	readonly fieldSearchString = 'searchString';
	readonly allSearchFields = 'allSearchFields';
	readonly allStatus = 'allStatus';
	readonly searchFieldOptions = 'searchFieldOptions';
	readonly statusOptions = 'statusOptions';

	private subscriptions: Subscription[] = [];
	readonly allSearchFieldChkbx: UntypedFormControl;
	readonly allStatusChkbx: UntypedFormControl;
	private readonly searchFieldChkbxs: { [p: string]: AbstractControl };
	private readonly statusChkbxs: { [p: string]: AbstractControl };

	searchFields: VerfahrenSearchKey[];

	public allFiltersSelected: Subject<boolean> = new Subject<boolean>();

	constructor(
		formBuilder: UntypedFormBuilder,
		private facade: VerfahrenFacade,
		private apiRootFacade: ApiRootFacade,
		private rendererFactory: RendererFactory2
	) {
		this.subscriptions.push(
			this.apiRootFacade
				.getCurrentApiRoot()
				.pipe(filter(apiRoot => !isNil(apiRoot)))
				.subscribe(apiRoot => {
					this.searchFields = apiRoot.features.searchableFields;
				})
		);
		// TODO: oben wird this.searchFields asynchron gesetzt, in der folgenden Methode wird der Wert synchron ausgelesen.
		// this.form soll aber readonly-Feld bleiben, da sonst diverse andere Zugriffe nicht mehr gehen
		this.form = this.createForm(formBuilder);
		this.setAllFormControlsToTrue(this.form);
		this.subscriptions.push(this.subscribeToSearchQuery());
		this.subscriptions.push(
			this.facade.verfahrenCreatedSubject?.subscribe(searchString => this.adaptSearchString(searchString))
		);

		this.allSearchFieldChkbx = <UntypedFormControl>this.form.get(this.allSearchFields);
		this.allStatusChkbx = <UntypedFormControl>this.form.get(this.allStatus);
		this.searchFieldChkbxs = (<UntypedFormGroup>this.form.get(this.searchFieldOptions)).controls;
		this.statusChkbxs = (<UntypedFormGroup>this.form.get(this.statusOptions)).controls;
	}

	private createForm(formBuilder: UntypedFormBuilder): UntypedFormGroup {
		return formBuilder.group({
			[this.fieldSearchString]: null,
			[this.allSearchFields]: true,
			[this.allStatus]: true,
			[this.searchFieldOptions]: this.createCheckboxFormGroup(this.searchFields, formBuilder),
			[this.statusOptions]: this.createCheckboxFormGroup(Object.keys(VerfahrenStatus), formBuilder),
		});
	}

	private createCheckboxFormGroup(options: string[], formBuilder: UntypedFormBuilder) {
		const config: { [key: string]: boolean } = {};
		options.forEach(option => (config[option] = true));
		return formBuilder.group(config);
	}

	private setAllFormControlsToTrue(group: UntypedFormGroup) {
		Object.values(group.controls).forEach(control => {
			if (control instanceof UntypedFormGroup) {
				this.setAllFormControlsToTrue(control);
				return;
			}
			control.setValue(true, { emitEvent: false });
		});
	}

	private subscribeToSearchQuery(): Subscription {
		return this.facade.verfahrenSearchBy.subscribe(query => {
			const newQuery = { ...query, filterByStatus: [query.filterByStatus] };
			this.form.patchValue(isNil(query) ? {} : newQuery);
		});
	}

	/**
	 * Hier wird immer eine Query übertragen. Je nach Inhalt entscheidet das Backend, ob es tatsächlich eine
	 * Suche ausführt oder bei fehlendem SuchString die schnellere getAll() -Methode verwendet.
	 *
	 * @param limit
	 */
	onSubmit(limit?: number) {
		const query = {
			limit,
			searchString: this.value.searchString === null ? '' : this.value.searchString,
			searchIn: this.getSearchFieldFilters(),
			filterByStatus: this.getStatusFilters(),
		};
		this.facade.searchVerfahren(query);
	}

	// Hier wird ein Text streaming heruntergeladen. Das kann der Browser einfacher als angular und unsere
	// download-Implementierung passt eh nicht.
	exportRegister() {
		const register = this.value.searchString;
		if ('SSR' === register || 'BSR' === register) {
			this.apiRootFacade.getCurrentApiRoot().subscribe(root => {
				const url = root._links[ApiRootLinkRel.EXPORT_REGISTER].href.replace('{register}', register);
				const renderer = this.rendererFactory.createRenderer(null, null);
				const anchor = renderer.createElement('a');
				renderer.setAttribute(anchor, 'href', url);
				renderer.setAttribute(anchor, 'target', '_blank');
				renderer.setAttribute(anchor, 'download', 'filename.csv');
				anchor.click();
			});
		}
	}

	getSearchFieldFilters(): string[] {
		return this.getCheckedKeys(this.searchFieldChkbxs);
	}

	getStatusFilters(): VerfahrenStatus[] {
		return this.getCheckedKeys(this.statusChkbxs) as VerfahrenStatus[];
	}

	private getCheckedKeys(controls: { [p: string]: AbstractControl }): string[] {
		return Object.entries(controls)
			.filter(([, control]) => control.value === true)
			.map(([key]) => key);
	}

	enoughFiltersSelected() {
		return (
			Object.values(this.searchFieldChkbxs).some(control => control.value === true) &&
			Object.values(this.statusChkbxs).some(control => control.value === true)
		);
	}

	get value(): VerfahrenSearchQuery {
		return { ...this.form.value };
	}

	adaptSearchString(searchString: string) {
		this.form.patchValue({ ...this.form.value, searchString });
	}

	reset() {
		this.adaptSearchString('');
		this.setSearchFieldFilters(this.searchFields);
		this.setStatusFilters(Object.keys(VerfahrenStatus));
		this.onSubmit();
	}

	setSearchFieldFilters(selectedKeys: string[]) {
		this.setCheckedKeys(this.searchFieldChkbxs, this.allSearchFieldChkbx, selectedKeys);
	}

	setStatusFilters(selectedKeys: string[]) {
		this.setCheckedKeys(this.statusChkbxs, this.allStatusChkbx, selectedKeys);
	}

	private setCheckedKeys(
		controls: { [p: string]: AbstractControl },
		allChkBox: UntypedFormControl,
		selectedKeys: string[]
	) {
		Object.entries(controls).forEach(([key, control]) => control.setValue(selectedKeys.includes(key)));
		allChkBox.setValue(Object.values(controls).every(control => control.value === true));
		this.updateAllFiltersSelected();
	}

	private updateAllFiltersSelected() {
		this.allFiltersSelected.next(this.allStatusChkbx.value && this.allSearchFieldChkbx.value);
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}
}
